/**
 * VENDOR MODULES
 */
import React from 'react';
import psl from 'psl';
import { getUrlParameter } from 'shared/utils/common';
import ScheduleSelectorBar from './ScheduleSelectorBar';
import { ScheduleSelectorSkeleton } from './ScheduleSelectorSkeleton';
import { EventSelectorSkeleton } from './EventSelectorSkeleton';
import NoEventsScreen from 'features/events/components/NoEventsScreen';
import EventSelector from 'features/events/components/EventSelector';
import UnifiedMainContent from './UnifiedMainContent';
import Betslip                  from 'features/tickets/components/Betslip';
import DialogBox from 'shared/components/DialogBox';
import UserSettings             from 'features/session/components/UserSettings';
import AppLoader                from 'shared/components/AppLoader';
import ToastHost               from 'shared/toast/ToastHost';
import { MobileTabsProvider, useMobileTabsContext } from 'app/stores/mobile-tabs-store';
import EVENT_NAMES              from 'shared/constants/events';
import Emitter                  from 'shared/events/emitter';
import { UPCOMING_EVENTS_LIST_SIZE } from 'features/events/api';
import { useSchedulesQuery, useUpcomingEventsQuery, useScheduleUpcomingQuery } from 'features/events/queries';
import { ScheduleProvider, useScheduleContext } from 'features/events/context/ScheduleContext';
import { useMiniTvRun } from 'features/video/stores';
import { useOngoingEventQuery } from 'features/video/queries';
import { getSportColor } from 'shared/utils/sport-colors';
import { deriveSportThemeCssVars } from 'shared/utils/sport-theme';
import { useServerNowSync } from 'shared/queries/serverTime';
import { openLoginModal } from 'mfe/auth-bridge';
import { isEmbeddedInPlatformShell } from 'mfe/embedding';

const RACING_SPORT_IDS = new Set([10, 11, 20, 21, 30]);
const BASEBALL_SPORT_ID = 40;
const FOOTBALL3D_SPORT_ID = 50;

/** Schedule selector: shows skeleton while schedules load, then ScheduleSelectorBar. */
function ScheduleSelectorWithData() {
  const { isPending } = useSchedulesQuery();
  if (isPending) {
    return <ScheduleSelectorSkeleton />;
  }
  return <ScheduleSelectorBar />;
}

/** Single code path: always renders EventSelector. Uses useQuery for schedule upcoming when scheduleId set; shows skeleton while pending so EventSelector never remounts on schedule change. */
function EventSelectorWithData({ scheduleId }: { scheduleId: number | string | null }) {
  const { isPending } = useScheduleUpcomingQuery({
    scheduleId,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });
  if (scheduleId != null && isPending) {
    return <EventSelectorSkeleton />;
  }
  return <EventSelector />;
}

        /***/
        function MainContent() {
  // Keep server-time query centralized (prevents many observers/refetch loops).
  useServerNowSync();
  const appRootRef = React.useRef<HTMLDivElement | null>(null);

  const [tvEventRunning, setTvEventRunning] = React.useState(false);
  const [tvAccordionExpanded, setTvAccordionExpanded] = React.useState(false);
  const [theaterMode, setTheaterMode] = React.useState(false);
  const { domain = '' } = (psl && psl.parse ? psl.parse(window.location.hostname) : {}) || {}

  React.useEffect(() => {
    if (!EVENT_NAMES.LOGIN_MODAL?.OPEN) return;
    const open = () => openLoginModal();
    Emitter.on(EVENT_NAMES.LOGIN_MODAL.OPEN, open);
    return () => Emitter.off(EVENT_NAMES.LOGIN_MODAL.OPEN, open);
  }, []);

  React.useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event?.data?.type === 'TV_EVENT_RUNNING') {
        setTvEventRunning(!!event.data.payload?.running);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  const domain_slug = domain? domain.replace(/\.+/g,'-'): 'caribe7-com';
  const { data: schedules = [] } = useSchedulesQuery();
  const current_schedule_id = useScheduleContext().currentScheduleId;

  const football3dScheduleId = React.useMemo(() => {
    const schedule = Array.isArray(schedules)
      ? schedules.find((s) => Number((s as { sport?: { sport_id?: number } })?.sport?.sport_id) === FOOTBALL3D_SPORT_ID)
      : null;
    return schedule?.schedule_id ?? null;
  }, [schedules]);

  const baseballScheduleIds = React.useMemo(() => {
    return (Array.isArray(schedules) ? schedules : [])
      .filter((s) => {
        const sportIdRaw = (s as { sport?: { sport_id?: number }; sport_id?: number })?.sport?.sport_id ?? (s as { sport_id?: number })?.sport_id;
        const sportId = sportIdRaw != null ? Number(sportIdRaw) : null;
        return sportId != null && Number.isFinite(sportId) && Number(sportId) === BASEBALL_SPORT_ID && (s as { schedule_id?: unknown })?.schedule_id != null;
      })
      .map((s) => s.schedule_id);
  }, [schedules]);

  // Check if desktop mode is forced via URL parameter.
  // Important: do NOT force desktop layout on mobile viewports.
  const desktopParam = getUrlParameter('desktop') === '1' || getUrlParameter('desktop') === true;
  const boxedParam = getUrlParameter('boxed');
  const embedded = isEmbeddedInPlatformShell();
  const boxedOff = boxedParam === '0' || boxedParam === 'false';
  const [isLgUp, setIsLgUp] = React.useState(false);

  React.useEffect(() => {
    const node = appRootRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const onResize = () => {
      const rootWidth = node.getBoundingClientRect().width;
      const viewportWidth = window.innerWidth;
      setIsLgUp(Math.min(rootWidth, viewportWidth) >= 1024);
    };

    onResize();
    const observer = new ResizeObserver(() => onResize());
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  const forceDesktop = desktopParam && isLgUp;
  const boxedMode = isLgUp && !boxedOff;

  const currentSchedule = Array.isArray(schedules) ? schedules.find((s) => s.schedule_id === current_schedule_id) : null;
  const currentSportIdRaw = (currentSchedule as { sport?: { sport_id?: number }; sport_id?: number })?.sport?.sport_id ?? (currentSchedule as { sport_id?: number })?.sport_id;
  const currentSportId = currentSportIdRaw != null ? parseInt(String(currentSportIdRaw), 10) : null;
  const isRacingSport =
    (currentSportId != null && RACING_SPORT_IDS.has(Number(currentSportId))) ||
    currentSchedule?.sport?.is_racing === true ||
    currentSchedule?.sport?.is_racing === 1 ||
    parseInt(String(currentSchedule?.sport?.is_racing ?? 0)) === 1;
  const isRacingSchedule = isRacingSport;
  const isBaseballSchedule =
    current_schedule_id != null &&
    Array.isArray(baseballScheduleIds) &&
    baseballScheduleIds.some((id) => String(id) === String(current_schedule_id));
  const isFootball3dSchedule =
    current_schedule_id != null && football3dScheduleId != null && String(football3dScheduleId) === String(current_schedule_id);
  const sportBaseColor = Number.isFinite(currentSportId) ? getSportColor(currentSportId as number) : null;
  const sportThemeVars = sportBaseColor ? deriveSportThemeCssVars(sportBaseColor) : null;

  const ongoingQuery = useOngoingEventQuery({
    scheduleId: current_schedule_id ?? undefined,
    enabled: Boolean(current_schedule_id != null),
  });
  const { getRun } = useMiniTvRun();
  const run = getRun(current_schedule_id != null ? current_schedule_id : '');
  const hasOngoingFromStore =
    ongoingQuery.data != null &&
    ongoingQuery.data.event_id != null &&
    String(ongoingQuery.data.event_id).trim() !== '';

  const scheduleSwitchGraceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scheduleSwitchGrace, setScheduleSwitchGrace] = React.useState(false);
  React.useEffect(() => {
    if (current_schedule_id == null) {
      setScheduleSwitchGrace(false);
      return;
    }
    if (scheduleSwitchGraceTimerRef.current != null) clearTimeout(scheduleSwitchGraceTimerRef.current);
    setScheduleSwitchGrace(true);
    scheduleSwitchGraceTimerRef.current = setTimeout(() => {
      setScheduleSwitchGrace(false);
      scheduleSwitchGraceTimerRef.current = null;
    }, 1500);
    return () => {
      if (scheduleSwitchGraceTimerRef.current != null) {
        clearTimeout(scheduleSwitchGraceTimerRef.current);
      }
    };
  }, [current_schedule_id]);

  const tvPanelRunning = (tvEventRunning || hasOngoingFromStore || scheduleSwitchGrace) && run?.phase !== 'ended';

  const { data: upcomingEvents = [] } = useUpcomingEventsQuery({
    scheduleId: current_schedule_id,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });
  const currentEventIdWhenTvRunning =
    isLgUp
      ? (ongoingQuery.data?.event_id ?? upcomingEvents?.[0]?.event_id ?? null)
      : null;

  React.useEffect(() => {
    if (embedded && typeof document !== 'undefined') {
      document.documentElement.classList.add('c7-virtual-embedded');
      document.body.classList.add('c7-virtual-embedded');
      return () => {
        document.documentElement.classList.remove('c7-virtual-embedded');
        document.body.classList.remove('c7-virtual-embedded');
      };
    }
  }, [embedded]);

  const { tabIndex } = useMobileTabsContext();
  const showSelectors = isLgUp || tabIndex === 0;
  const appRootStyle = embedded
    ? undefined
    : { minHeight: '100dvh', height: '100dvh' };

  return (
        <div
          id="c7-virtual-app-root"
          ref={appRootRef}
          className={`relative isolate w-full flex flex-1 min-h-0 flex-col ${embedded ? 'overflow-visible' : 'overflow-hidden'}`}
          style={appRootStyle}
        >
        <div
          id="wrapper"
          className={`w-full flex flex-1 min-h-0 flex-col ${embedded ? 'overflow-visible' : 'overflow-hidden'} c7-app-bg pt-0 ${embedded ? 'c7-embedded-in-shell' : ''} ${forceDesktop ? 'force-desktop' : ''} ${boxedMode ? 'c7-boxed' : ''}`}
          data-theme-domain={String(domain_slug ?? 'caribe7-com')}
          style={{
            ...(embedded ? {} : { height: '100%' }),
            ...(forceDesktop ? { minWidth: '768px' } : {}),
            ...(boxedMode ? { maxWidth: '1280px', marginLeft: 'auto', marginRight: 'auto' } : {}),
            ...(sportThemeVars || {})
          }}
        >
          {showSelectors && (
            <>
              {/* Schedule selector - Fixed at top */}
              <ScheduleSelectorWithData />

              {/* Event Selector - Fixed underneath sport buttons */}
              <EventSelectorWithData scheduleId={current_schedule_id} />
            </>
          )}

          {/* Main Content */}
          <div className="w-full min-h-0 flex flex-1 flex-col">
            <UnifiedMainContent
              isLgUp={isLgUp}
              tvPanelRunning={tvPanelRunning}
              tvAccordionExpanded={tvAccordionExpanded}
              setTvAccordionExpanded={setTvAccordionExpanded}
              theaterMode={theaterMode}
              setTheaterMode={setTheaterMode}
              currentEventIdWhenTvRunning={currentEventIdWhenTvRunning}
              embedded={embedded}
              onOpenLogin={() => openLoginModal()}
            />
          </div>
        </div>

        <DialogBox/>
        <ToastHost/>

        {/* User Settings - Fixed position, renders independently */}
        <UserSettings/>
        
        <Betslip isLgUp={isLgUp} />
        </div>
  );
}

function MainWrapper() {
  const { data: schedules = [], isLoading: loading } = useSchedulesQuery();

  // Filter schedules to show only active ones with embedded sport info
  // This matches the filter logic in ScheduleSelectorBar (active schedules only).
  const available_schedules = React.useMemo(() => {
    return (Array.isArray(schedules) ? schedules : [])
      .filter((schedule) => {
        const sportObj = schedule.sport;
        return sportObj && (sportObj.active === true || sportObj.active === 1 || parseInt(String(sportObj.active ?? 0), 10) === 1);
      });
  }, [schedules]);

  // Show NoEventsScreen when schedules have finished loading and there are no active schedules
  const showNoEvents = !loading && available_schedules.length === 0;

  return (
    <AppLoader>
      {showNoEvents ? <NoEventsScreen /> : (
        <ScheduleProvider>
          <MobileTabsProvider>
            <MainContent />
          </MobileTabsProvider>
        </ScheduleProvider>
      )}
    </AppLoader>
  );
}

export default MainWrapper;